import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import PropTypes from 'prop-types';
import Loader from '../../../../atoms/icons/loader';
import { useQuery } from 'react-query';
import EmptyState from '../empty-state';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from "react-redux";
import get from "lodash/get";
import { isWithinTradingHours } from "../../../../../helpers/utils";
import './futures-graph-widget.m.css';

const OpenInterestChangeWidget = ({
  maintainAspectRatio = true,
  responsive = true,
  stockName,
  loadingStatus,
  countStatus,
  flag
}) => {
  const [futuresChartData, setFuturesChartData] = useState({});
  const [selectedStock, setSelectedStock] = useState(stockName);
  const [selectedDuration, setSelectedDuration] = useState('1M');
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [dataForInterestChange, setDataForInterestChange] = useState({});
  const requiredLabels = [];
  const callOfChangeData = [];
  const putOfChangeData = [];
  const callsData = [];
  const putsData = [];
  const queryKey = flag === 'change' ? 'getChartDataFutures' : 'getChartDataDistFutures';
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], [])
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {})
  );

  const barRef = useRef();

  const getChartDataFutures = async (stockName, duration) => {
    const response = await fetch(`/foapi/future/getDistribution?exchangeSymbol=${stockName}&type=${duration}`);
    return response.json();
  };

  const resultChartDataForFutures = useQuery(
    queryKey,
    () => getChartDataFutures(selectedStock, selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: data => {
        setFuturesChartData(data);
        setData(data);
      },
      enabled: initialLoading || (!!selectedStock && !!selectedDuration && isWithinTradingHours(tradingHolidays, specialTradingDays)),
      cacheTime: 0
    }
  );

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    durationChangeHandler('1M');
  }, [stockName]);

  useEffect(() => {
    if (countStatus !== 0) {
      backToDefault();
    }
  }, [countStatus]);

  useEffect(() => {
    if (resultChartDataForFutures?.isSuccess) {
      setFuturesChartData(resultChartDataForFutures?.data);
    }

    setData(futuresChartData);
  }, [resultChartDataForFutures?.isSuccess, selectedDuration, timePeriod]);

  const setData = () => {
    futuresChartData &&
    futuresChartData?.data
      ?.filter(chartValue => {
        return chartValue.type === 'call';
      })
      .map(filteredValue => {
        requiredLabels.push(filteredValue['strike-price']);
        callOfChangeData.push(filteredValue['open-interest-change']);
        callsData.push(filteredValue['open-interest']);
      });

    futuresChartData &&
    futuresChartData?.data
      ?.filter(chartValue => {
        return chartValue.type === 'put';
      })
      .map(filteredValue => {
        putOfChangeData.push(filteredValue['open-interest-change']);
        putsData.push(filteredValue['open-interest']);
      });

    setDataForInterestChange(getDataForInterestChange());
    setTimeout(() => barRef.current?.update(), 2000);
  };
  const backToDefault = () => {
    setSelectedStock('NIFTY 50');
    setTimePeriod('monthly');
    setSelectedDuration('1M');
    durationChangeHandler('1M');
  };

  const options = {
    animation: false,
    responsive: responsive,
    maintainAspectRatio: maintainAspectRatio,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'right'
      },
      title: {
        display: true,
        text: ''
      },
      scales: {
        y: {
          ticks: {
            display: false
          },
          grid: {
            drawBorder: false,
            display: false
          }
        }
      },
      tooltip: {
        intersect: false
      }
    }
  };

  const getDataForInterestChange = () => {
    const callLabel = flag === 'change' ? 'Call OI Change' : 'Calls';
    const putLabel = flag === 'change' ? 'Put OI Change' : 'Puts';
    const callData = flag === 'change' ? callOfChangeData : callsData;
    const putData = flag === 'change' ? putOfChangeData : putsData;
    return {
      labels: requiredLabels,
      datasets: [
        {
          label: callLabel,
          data: callData,
          backgroundColor: '#14C588'
        },
        {
          label: putLabel,
          data: putData,
          backgroundColor: '#d01e51'
        }
      ]
    };
  };

  const timePeriodHandler = event => {
    const period = event.target.value === 'monthly' ? '1M' : event.target.value === 'weekly' ? '1W' : '1C';
    durationChangeHandler(period);
    setTimePeriod(event.target.value);
    setSelectedDuration(period);
  };

  const durationChangeHandler = async timeValueSelected => {
    setLoading(true);
    const data = await getChartDataFutures(stockName, timeValueSelected);
    setFuturesChartData(data);
    setLoading(false);
  };

  return (
    <div styleName="chart-filter-container seperator">
      <span styleName="chart-title">{flag === 'change' ? 'Open Interest Change' : 'Open Interest Distribution'}</span>

      <div styleName="filter-container">
        <div styleName="time-filters">
          <button
            styleName={`time-btn ${['1M', '1W', '1C'].includes(selectedDuration) ? 'selected' : ''}`}
            onClick={() => {
              durationChangeHandler(timePeriod === 'monthly' ? '1M' : timePeriod === 'weekly' ? '1W' : '1C');
              setSelectedDuration(timePeriod === 'monthly' ? '1M' : timePeriod === 'weekly' ? '1W' : '1C');
            }}
          >
            {timePeriod === 'monthly' ? '1M' : timePeriod === 'weekly' ? '1W' : '1C'}
          </button>
          <button
            styleName={`time-btn ${['2M', '2W', '2C'].includes(selectedDuration) ? 'selected' : ''}`}
            onClick={() => {
              durationChangeHandler(timePeriod === 'monthly' ? '2M' : timePeriod === 'weekly' ? '2W' : '2C');
              setSelectedDuration(timePeriod === 'monthly' ? '2M' : timePeriod === 'weekly' ? '2W' : '2C');
            }}
          >
            {timePeriod === 'monthly' ? '2M' : timePeriod === 'weekly' ? '2W' : '2C'}
          </button>
          <button
            styleName={`time-btn ${['3M', '3W', '3C'].includes(selectedDuration) ? 'selected' : ''}`}
            onClick={() => {
              durationChangeHandler(timePeriod === 'monthly' ? '3M' : timePeriod === 'weekly' ? '3W' : '3C');
              setSelectedDuration(timePeriod === 'monthly' ? '3M' : timePeriod === 'weekly' ? '3W' : '3C');
            }}
          >
            {timePeriod === 'monthly' ? '3M' : timePeriod === 'weekly' ? '3W' : '3C'}
          </button>
        </div>

        <div styleName="dropdown-filter">
          <select
            id="time"
            name="time"
            styleName="select-btn"
            onChange={timePeriodHandler}
            value={timePeriod}
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="cumulativeMonthly">Cumulative Monthly</option>
          </select>
        </div>
      </div>

      <div styleName="graph-container">
        {loading || loadingStatus ? (
          <div styleName="loader-container-ext">
            <div styleName="loader-container-int">
              <Loader color={'#dadee1'} />
            </div>
          </div>
        ) : isEmpty(futuresChartData) ? (
          <div styleName="no-data-container-ext-chart">
            <div styleName="no-data-container-int">
              <EmptyState />
            </div>
          </div>
        ) : (
          <Bar options={options} data={dataForInterestChange} ref={barRef} redraw={true} height="250px" width="300px" />
        )}
      </div>
    </div>
  );
};

OpenInterestChangeWidget.propTypes = {
  maintainAspectRatio: PropTypes.bool,
  responsive: PropTypes.bool,
  stockName: PropTypes.string.isRequired,
  loadingStatus: PropTypes.bool,
  countStatus: PropTypes.number
};

export default OpenInterestChangeWidget;
